import { DeliveryZone, DeliveryZoneArea, NeighborhoodWithPolygon } from '@/types/delivery-zone.types';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Switch } from '@/components/ui/switch';
import { MapPin, Loader2, ArrowRight, Search } from 'lucide-react';
import { useState, useMemo } from 'react';
import { cn } from '@/lib/utils';
import { Input } from '@/components/ui/input';

interface ZoneAreasListProps {
  zone: DeliveryZone | null;
  zoneAreas: DeliveryZoneArea[];
  allNeighborhoods: NeighborhoodWithPolygon[];
  neighborhoodsLoading: boolean;
  selectedNeighborhoodId?: string;
  onNeighborhoodSelect?: (neighborhoodId: string) => void;
  onToggleNeighborhood: (neighborhoodId: string) => void;
  togglingNeighborhoodId?: string | null;
  onRefresh: () => void;
  onBack: () => void;
}

export function ZoneAreasList({
  zone,
  zoneAreas,
  allNeighborhoods,
  neighborhoodsLoading,
  selectedNeighborhoodId,
  onNeighborhoodSelect,
  onToggleNeighborhood,
  togglingNeighborhoodId,
  onBack,
}: ZoneAreasListProps) {
  const [searchQuery, setSearchQuery] = useState('');

  // Create lookup set for neighborhoods in zone
  const neighborhoodIdsInZone = useMemo(() => 
    new Set(zoneAreas.map(area => area.neighborhood_id)), 
    [zoneAreas]
  );

  // Filter neighborhoods by search query
  const filteredNeighborhoods = useMemo(() => {
    if (!searchQuery.trim()) return allNeighborhoods;
    const query = searchQuery.toLowerCase();
    return allNeighborhoods.filter(n => 
      n.name.toLowerCase().includes(query) ||
      n.name_ar.toLowerCase().includes(query) ||
      n.name_en?.toLowerCase().includes(query)
    );
  }, [allNeighborhoods, searchQuery]);

  // Sort: in-zone first, then available
  const sortedNeighborhoods = useMemo(() => {
    return [...filteredNeighborhoods].sort((a, b) => {
      const aInZone = neighborhoodIdsInZone.has(a.id);
      const bInZone = neighborhoodIdsInZone.has(b.id);
      if (aInZone && !bInZone) return -1;
      if (!aInZone && bInZone) return 1;
      return a.name_ar.localeCompare(b.name_ar, 'ar');
    });
  }, [filteredNeighborhoods, neighborhoodIdsInZone]);

  const inZoneCount = zoneAreas.length;
  const availableCount = allNeighborhoods.length - inZoneCount;

  if (!zone) {
    return (
      <div className="flex flex-col items-center justify-center h-full py-12 text-center">
        <MapPin className="h-12 w-12 text-muted-foreground/50 mb-4" />
        <p className="text-muted-foreground">
          اختر منطقة لعرض الأحياء
        </p>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center gap-3 pb-4 border-b mb-4">
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          onClick={onBack}
        >
          <ArrowRight className="h-4 w-4" />
        </Button>
        <div className="flex-1">
          <h3 className="font-semibold flex items-center gap-2">
            <MapPin className="h-4 w-4 text-primary" />
            أحياء {zone.name}
          </h3>
          <p className="text-xs text-muted-foreground">{zone.code}</p>
        </div>
        <Badge variant={zone.is_active ? 'default' : 'secondary'}>
          {zone.is_active ? 'نشط' : 'غير نشط'}
        </Badge>
      </div>

      {/* Stats */}
      <div className="flex gap-2 mb-4">
        <div className="flex-1 p-2 bg-green-50 dark:bg-green-950/30 rounded-lg text-center">
          <p className="text-lg font-bold text-green-600">{inZoneCount}</p>
          <p className="text-xs text-muted-foreground">في المنطقة</p>
        </div>
        <div className="flex-1 p-2 bg-slate-50 dark:bg-slate-950/30 rounded-lg text-center">
          <p className="text-lg font-bold text-slate-600">{availableCount}</p>
          <p className="text-xs text-muted-foreground">متاح</p>
        </div>
      </div>

      {/* Search */}
      <div className="relative mb-4">
        <Search className="absolute end-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
        <Input
          placeholder="بحث عن حي..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="pe-9"
        />
      </div>

      {/* Loading state */}
      {neighborhoodsLoading && (
        <div className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-primary" />
        </div>
      )}

      {/* Neighborhoods List */}
      {!neighborhoodsLoading && (
        <ScrollArea className="flex-1">
          {sortedNeighborhoods.length > 0 ? (
            <div className="space-y-2">
              {sortedNeighborhoods.map((neighborhood) => {
                const inZone = neighborhoodIdsInZone.has(neighborhood.id);
                const isSelected = selectedNeighborhoodId === neighborhood.id;
                const isToggling = togglingNeighborhoodId === neighborhood.id;

                return (
                  <div
                    key={neighborhood.id}
                    className={cn(
                      "flex items-center justify-between p-3 rounded-lg border bg-card cursor-pointer transition-colors",
                      isSelected && "ring-2 ring-primary bg-primary/5",
                      inZone 
                        ? "hover:bg-green-50 dark:hover:bg-green-950/20 border-green-200 dark:border-green-900" 
                        : "hover:bg-slate-50 dark:hover:bg-slate-950/20"
                    )}
                    onClick={() => onNeighborhoodSelect?.(neighborhood.id)}
                  >
                    <div className="flex items-center gap-3 flex-1">
                      <div 
                        className={cn(
                          "w-3 h-3 rounded-full",
                          inZone ? "bg-green-500" : "bg-slate-400"
                        )} 
                      />
                      <div>
                        <p className="font-medium text-sm">
                          {neighborhood.name_ar || neighborhood.name}
                        </p>
                        {neighborhood.name_en && (
                          <p className="text-xs text-muted-foreground">
                            {neighborhood.name_en}
                          </p>
                        )}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {isToggling ? (
                        <Loader2 className="h-4 w-4 animate-spin text-primary" />
                      ) : (
                        <Switch
                          checked={inZone}
                          onCheckedChange={() => onToggleNeighborhood(neighborhood.id)}
                          onClick={(e) => e.stopPropagation()}
                        />
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <MapPin className="h-10 w-10 text-muted-foreground/50 mb-3" />
              <p className="text-muted-foreground text-sm">
                {searchQuery ? 'لا توجد نتائج للبحث' : 'لا توجد أحياء متاحة'}
              </p>
            </div>
          )}
        </ScrollArea>
      )}
    </div>
  );
}
